import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { getUserTemplateCount, saveTemplate } from '../../firebase/services';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const Modal = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.large};
  padding: ${theme.spacing.xl};
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: ${theme.shadows.large};
  animation: slideUp 0.3s ease-in-out;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const SuccessIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing.md};
  font-size: 32px;
`;

const Title = styled.h2`
  font-family: ${theme.typography.headingFont};
  font-size: ${theme.typography.fontSizes.xlarge};
  color: ${theme.colors.deepNavy};
  margin: 0 0 ${theme.spacing.sm} 0;
  font-weight: bold;
`;

const Subtitle = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.medium};
  color: ${theme.colors.slateGray};
  margin: 0 0 ${theme.spacing.lg} 0;
  line-height: 1.5;
`;

const ButtonPrimary = styled.button`
  background: linear-gradient(135deg, ${theme.colors.brandYellow} 0%, #FFC700 100%);
  color: ${theme.colors.deepNavy};
  border: none;
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.medium};
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin-bottom: ${theme.spacing.md};
  transition: all 0.2s ease-in-out;
  box-shadow: 0 4px 8px rgba(255, 209, 0, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(255, 209, 0, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ButtonSecondary = styled.button`
  background-color: transparent;
  color: ${theme.colors.slateGray};
  border: 1px solid ${theme.colors.softGray};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.medium};
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: ${theme.colors.background};
    border-color: ${theme.colors.slateGray};
  }
`;

const TemplateLink = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.slateGray};
  font-family: ${theme.typography.bodyFont};
  font-size: 13px;
  cursor: pointer;
  margin-top: 12px;
  padding: 4px 0;

  &:disabled {
    cursor: default;
  }
`;

const SuccessModal = ({ onShareNow, onMaybeLater, activityData, userId }) => {
  const { t } = useTranslation();
  const [templateSaveState, setTemplateSaveState] = useState('idle');

  const handleSaveTemplate = async () => {
    setTemplateSaveState('saving');
    try {
      const count = await getUserTemplateCount(userId);
      if (count >= 20) {
        alert(t('templates.maxReached'));
        setTemplateSaveState('idle');
        return;
      }
      await saveTemplate({
        userId,
        title: activityData.title,
        causeTitle: activityData.causeTitle,
        causeId: activityData.causeId,
        levelTitle: activityData.levelTitle,
      });
      setTemplateSaveState('saved');
      setTimeout(() => setTemplateSaveState('hidden'), 2000);
    } catch (error) {
      console.error('Failed to save template:', error);
      setTemplateSaveState('idle');
    }
  };

  const templateLinkText =
    templateSaveState === 'saving' ? t('templates.saving') :
    templateSaveState === 'saved' ? t('templates.templateSaved') :
    t('templates.saveAsTemplate');

  return (
    <Overlay onClick={onMaybeLater}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <SuccessIcon>✓</SuccessIcon>
        <Title>{t('success.title')}</Title>
        <Subtitle>
          {t('success.message')}
        </Subtitle>

        <ButtonPrimary onClick={onShareNow}>
          {t('success.shareNow')}
        </ButtonPrimary>

        <ButtonSecondary onClick={onMaybeLater}>
          {t('success.maybeLater')}
        </ButtonSecondary>

        {templateSaveState !== 'hidden' && activityData && (
          <TemplateLink
            onClick={handleSaveTemplate}
            disabled={templateSaveState === 'saving' || templateSaveState === 'saved'}
          >
            {templateLinkText}
          </TemplateLink>
        )}
      </Modal>
    </Overlay>
  );
};

export default SuccessModal;
